import {
  ChevronRightIcon,
  FlexBox,
  JoonUIColor,
  Modal,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { create } from "zustand"

import Shine from "../../components/coaching/Shine"
import { isCoachingAvailable } from "../../hooks/useIsCoachingAvailable"
import useMobile from "../../hooks/useMobile"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { localStorageItems } from "../../util/storage"
import { useAddQuestTemplatesModalStore } from "../questBoard/addQuestTemplates/useAddQuestTemplatesModalStore"
import { useReviewQuestModalStore } from "../questBoard/useReviewQuestModalStore"
import { useViewQuestModalStore } from "../questBoard/viewQuestModal/useViewQuestModalStore"

export enum CoachUpsellModalType {
  reject = "reject",
  delete = "delete",
}

interface CoachUpsellModalStore {
  isOpen: boolean
  onOpen: (type: CoachUpsellModalType) => void
  onClose: () => void

  type: CoachUpsellModalType | undefined
}

export const useCoachUpsellModalStore = create<CoachUpsellModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: (type: CoachUpsellModalType) => {
      // Gate here rather than at each of the quest reject/delete call sites — this
      // is the single door into the modal, and it keeps the "seen date" unwritten
      // so the moment is still available if the user switches back to English.
      if (!isCoachingAvailable()) return

      trackAnalyticEvent(ANALYTIC_EVENTS.COACH_UPSELL_MODAL_VIEWED, {
        type,
      })
      localStorage.setItem(
        type === CoachUpsellModalType.reject
          ? localStorageItems.rejectCoachUpsellModalSeenDate
          : localStorageItems.deleteCoachUpsellModalSeenDate,
        dayjs().format("YYYY-MM-DD")
      )
      set({ isOpen: true, type })
    },
    onClose: () => {
      set({ isOpen: false })
    },

    type: undefined,
  })
)

const CoachUpsellModal = () => {
  const { isOpen, onClose, type } = useCoachUpsellModalStore()
  const { onClose: onCloseAddTemplateModal } = useAddQuestTemplatesModalStore()
  const { onClose: onCloseViewQuestModal } = useViewQuestModalStore()
  const { onClose: onCloseReviewQuestModal } = useReviewQuestModalStore()

  const isMobile = useMobile()
  const router = useRouter()

  const onLearnMoreClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.COACH_UPSELL_MODAL_LEARN_MORE, {
      type,
    })
    router.push("/coaching?from=preview&popup=true")
    onClose()
    onCloseAddTemplateModal()
    onCloseViewQuestModal()
    onCloseReviewQuestModal()
  }

  const onMaybeLaterClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.COACH_UPSELL_MODAL_DISMISS, {
      type,
    })
    onClose()
    onCloseAddTemplateModal()
    onCloseViewQuestModal()
    onCloseReviewQuestModal()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "min(500px, 100%)" }}
    >
      <FlexBox
        direction="column"
        align="center"
        wrap={false}
        gap={SPACING.space6}
        style={{
          borderRadius: SPACING.space2,
          boxShadow: "4px 6px 6px 0px rgba(0, 0, 0, 0.08)",
          background: JoonUIColor.background.primaryNeutral,
          position: "relative",
          padding: SPACING.space4,
          paddingTop: SPACING.space9,
          paddingBottom: SPACING.space9,
        }}
        fullWidth
      >
        <FlexBox
          direction="column"
          align="center"
          gap={SPACING.space4}
          wrap={false}
          style={{
            width: "fit-content",
          }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            style={{ width: "min(300px, 90%)" }}
          >
            {type === CoachUpsellModalType.reject
              ? "Having trouble getting your kids to do their quests properly?"
              : "Need help setting up the best routines for your child?"}
          </Typography>
          <Typography
            variant="bodySmall"
            textAlign="center"
            style={{ width: "min(380px, 90%)" }}
          >
            Connect 1:1 with a Joon coach and{" "}
            <Typography variant="bodySmallBold">
              get actionable personalized strategies to improve your child's
              routines
            </Typography>
          </Typography>
        </FlexBox>

        <img
          src={`/images/guidePhotos/care_breakdown_strategy.svg`}
          style={{ width: "min(350px, 90%)" }}
          alt="<3"
        />

        <button
          style={{
            position: "relative",
            width: "min(380px, 90%)",
            padding: 0,
          }}
          onClick={onLearnMoreClick}
        >
          <FlexBox
            direction="row"
            align="center"
            justify="space-between"
            wrap={false}
            style={{
              background: JoonUIColor.background.accent,
              borderRadius: SPACING.space2,
              padding: `${SPACING.space2} ${SPACING.space1} ${SPACING.space2} ${SPACING.space3}`,
              marginTop: SPACING.space4,
              boxShadow: "4px 4px 6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            <Typography
              variant={isMobile ? "bodySmallBold" : "bodyBold"}
              color={JoonUIColor.text.inverted}
              style={{ lineHeight: "1.4" }}
            >
              Learn more about Joon Coaching
            </Typography>

            <div style={{ minWidth: "30px" }}>
              <ChevronRightIcon color={JoonUIColor.icon.inverted} size={24} />
            </div>
          </FlexBox>
          <Shine />
        </button>

        <button onClick={onMaybeLaterClick}>
          <Typography
            variant={isMobile ? "bodySmall" : "body"}
            color={JoonUIColor.text.primaryAccent}
          >
            Maybe later
          </Typography>
        </button>
      </FlexBox>
    </Modal>
  )
}

export default CoachUpsellModal
